import { Peer } from "peerjs";
import type { MediaConnection, DataConnection } from "peerjs";
import { peerHost, getIceServers, iceTransportPolicy } from "./config";
import { makeCode } from "./words";

export interface PeerSession {
  peer: Peer;
  code: string;
}

// Creates a Peer registered under a fresh word code. If the broker reports the
// code is already taken, regenerates and retries a few times.
export async function createSession(maxRetries = 5): Promise<PeerSession> {
  const iceServers = await getIceServers();

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const code = makeCode();
    try {
      const peer = await openPeer(code, iceServers);
      return { peer, code };
    } catch (err) {
      if (isTakenError(err) && attempt < maxRetries - 1) continue;
      throw err;
    }
  }
  throw new Error("Could not register a call code. Please reload.");
}

function openPeer(code: string, iceServers: RTCIceServer[]): Promise<Peer> {
  return new Promise((resolve, reject) => {
    const peer = new Peer(code, {
      host: peerHost.host,
      port: peerHost.port,
      path: peerHost.path,
      secure: peerHost.secure,
      key: peerHost.key,
      config: { iceServers, iceTransportPolicy },
      debug: 1,
    });
    const onOpen = () => {
      peer.off("error", onError);
      resolve(peer);
    };
    const onError = (err: Error) => {
      peer.off("open", onOpen);
      peer.destroy();
      reject(err);
    };
    peer.once("open", onOpen);
    peer.once("error", onError);
  });
}

function isTakenError(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    "type" in err &&
    (err as { type: string }).type === "unavailable-id"
  );
}

export type { MediaConnection, DataConnection };
